import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { PhotoCarousel } from "../components/PhotoCarousel";
import { CANDIDATE, STOCK, PHOTOS, CAMPAIGN_PHOTOS } from "../lib/config";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HeroLine = ({ children, i }) => (
  <span className="block overflow-hidden pl-[0.22em] -ml-[0.22em] pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      variants={lineVariants}
      custom={i}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.62, 0.85]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%]">
          <img
            src={STOCK.flag}
            alt="American flag waving"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[hsl(var(--navy))]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--navy))] via-transparent to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 md:px-8 pb-20 md:pb-28 pt-32">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="text-xs md:text-sm uppercase tracking-overline font-bold text-red-400 mb-6"
            data-testid="hero-eyebrow"
          >
            Ontario County Treasurer
          </motion.p>

          <h1 className="font-display font-black text-white text-6xl md:text-8xl lg:text-9xl leading-[0.92] tracking-tight">
            <HeroLine i={0}>Jeffrey</HeroLine>
            <HeroLine i={1}>Trickler</HeroLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl"
          >
            <p className="text-lg md:text-xl text-slate-100 font-medium leading-snug">
              {CANDIDATE.subhead}
              <br />
              {CANDIDATE.office}
            </p>
            <p className="mt-4 font-display italic text-xl md:text-2xl text-red-300">
              {CANDIDATE.tagline}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/about"
              data-testid="hero-meet-jeff-btn"
              className="focus-ring group inline-flex items-center justify-center gap-2 bg-white text-[hsl(var(--navy))] font-semibold px-8 py-4 rounded-sm hover:bg-slate-100 transition-colors"
            >
              Meet Jeff
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/donate"
              data-testid="hero-support-btn"
              className="focus-ring inline-flex items-center justify-center gap-2 bg-red-700 text-white font-semibold px-8 py-4 rounded-sm hover:bg-red-800 transition-colors"
            >
              Support the Campaign
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 right-8 hidden md:flex items-center gap-2 text-white/70 text-xs uppercase tracking-overline"
        >
          Scroll <ArrowDown size={16} className="animate-bounce" />
        </motion.div>
      </section>

      <EditorialMarquee />

      {/* ABOUT TEASER */}
      <section className="py-24 md:py-32 bg-background" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
            <Reveal className="md:col-span-6 lg:col-span-5">
              <div className="relative">
                <div className="overflow-hidden clip-frame border border-slate-200 bg-white">
                  <img
                    src={PHOTOS.headshot}
                    alt="Jeffrey Trickler, Ontario County Treasurer"
                    className="w-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-5 -right-4 md:-right-6 bg-[hsl(var(--navy))] text-white px-6 py-4">
                  <p className="font-mono-num text-3xl font-bold text-red-400 leading-none">4+</p>
                  <p className="text-xs uppercase tracking-widest mt-1">Years in office</p>
                </div>
              </div>
            </Reveal>

            <div className="md:col-span-6 lg:col-span-6 lg:col-start-7">
              <Reveal>
                <p className="text-sm uppercase tracking-overline font-bold text-red-700">About the Candidate</p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight text-[hsl(var(--navy))] text-balance">
                  Experienced leadership for the taxpayers of Ontario County.
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                  Jeff Trickler is seeking reelection as Ontario County Treasurer. His time in office,
                  combined with prior public service as Chief of Police for the City of Geneva, has given
                  him the leadership, financial oversight, and administrative expertise the role demands.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                  He leads with fairness, consistency, and accountability — and holds the confidence of the
                  county departments that work closely with the Treasurer's Office.
                </p>
              </Reveal>
              <Reveal delay={0.4}>
                <Link
                  to="/about"
                  data-testid="teaser-readmore-btn"
                  className="focus-ring group mt-8 inline-flex items-center gap-2 font-semibold text-[hsl(var(--navy))] border-b-2 border-red-700 pb-1 hover:gap-3 transition-all"
                >
                  Read Jeff's full story
                  <ArrowRight size={18} className="text-red-700" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PHOTO WHEEL */}
      <section className="py-24 md:py-28 bg-white border-y border-slate-200" data-testid="home-gallery">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal>
              <p className="text-sm uppercase tracking-overline font-bold text-red-700">On the Ground</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight text-[hsl(var(--navy))]">
                Serving the community, every day.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <PhotoCarousel photos={CAMPAIGN_PHOTOS} />
          </Reveal>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 md:py-32 bg-[hsl(var(--navy))] text-white paper-grain" data-testid="home-cta">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight text-balance">
              Keep experienced, accountable leadership in the Treasurer's Office.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/donate"
                data-testid="cta-donate-btn"
                className="focus-ring inline-flex items-center justify-center gap-2 bg-red-700 text-white font-semibold px-8 py-4 rounded-sm hover:bg-red-800 transition-colors"
              >
                Support the Campaign
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/accomplishments"
                data-testid="cta-accomplishments-btn"
                className="focus-ring inline-flex items-center justify-center gap-2 border border-white/40 text-white font-semibold px-8 py-4 rounded-sm hover:bg-white/10 transition-colors"
              >
                See the Record
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
